/**
 * The one TSCN scanning loop, shared by renderer and linter, with no three.js
 * dependency. A node-creator callback builds each node. An optional ParseObserver lets
 * the strict path collect errors and run validators. With no observer the loop keeps
 * the lenient skip-and-continue recovery.
 */
import { resolveDeprecatedProperty } from '../godot/classdb/deprecated.js';
import { parseHeading, parseProperty, isHeading, isSectionHeading, isEmpty, scanValueChunk, isIncompleteState, INITIAL_SCAN_STATE, stripLineComment, splitLines, } from './utils.js';
import { parseExternalResource, parseInternalResource } from './resourceParsers.js';
import { seatScene } from './sceneTreeBuilder.js';
import { refusesToInstantiate } from './instantiateRefusal.js';
import * as logger from '../logger.js';
import { nodeOrigin } from './rawNode.js';
import { simplifiedNodePath } from '../godot/string/nodePath.js';
/**
 * Lenient rendering uses the loop bare. Strict linting attaches a ParseObserver (see
 * StrictTscnParser) to collect errors and run validators.
 */
export class TscnParserCore {
    /**
     * Parse TSCN content using a custom node creator
     * @param content - Raw TSCN file content
     * @param nodeCreator - Callback to create nodes (renderer-specific or linter-specific)
     * @param observer - Optional hooks for strict consumers (errors, sections, properties)
     * @returns The scene and its headings
     */
    parse(content, nodeCreator, observer) {
        // Debug, not info: an editor host parses on each keystroke.
        logger.debug('[Parser] Starting TSCN parsing');
        const lines = splitLines(content);
        // Every node beside its heading's line, in scan order. The line stays off the node:
        // the orphan report reads it here, and a strict observer gets it from `onSectionBuilt`.
        // One array, not a parallel `nodes` list, which two push sites would keep in step.
        const origins = [];
        const externalResources = [];
        const internalResources = [];
        // One per file: the loader refuses any tag after the `[resource]` body
        // (`resource_format_text.cpp:837-841`), so a later one is a corrupt file, and the last one read stays.
        let mainResource;
        const editableInstances = [];
        let currentSection = 'none';
        let currentHeading = null;
        let currentHeadingLine = 0;
        // The type a `.tres` header declares. Its `[resource]` body has no type:
        // `res_type` comes from the header (resource_format_text.cpp:1166) and
        // `ClassDB::instantiate(res_type)` builds it when the tag opens (:741). A `.tscn`
        // has no such header, so this stays undefined there.
        let headerResourceType;
        let currentProperties = {};
        // A string value whose quote does not close on its own line. Raw lines append
        // until the quote balances. The array is joined once, and `scanState` carries
        // the balance scan forward one chunk at a time: O(total length), where a `+=`
        // plus a full rescan per line is O(length^2).
        let pendingMultiline = null;
        const finalizeSection = () => {
            if (!currentHeading)
                return;
            if (currentSection === 'node') {
                const node = nodeCreator(currentHeading, currentProperties);
                if (node) {
                    origins.push(nodeOrigin(currentHeading, node, currentHeadingLine));
                    observer?.onSectionBuilt?.(node, currentHeadingLine);
                }
            }
            else if (currentSection === 'ext_resource') {
                const resource = parseExternalResource(currentHeading);
                if (resource) {
                    externalResources.push(resource);
                    observer?.onSectionBuilt?.(resource, currentHeadingLine);
                }
            }
            else if (currentSection === 'sub_resource') {
                const resource = parseInternalResource(currentHeading, currentProperties);
                if (resource) {
                    internalResources.push(resource);
                    observer?.onSectionBuilt?.(resource, currentHeadingLine);
                }
            }
            else if (currentSection === 'resource') {
                mainResource = { data: currentProperties };
                observer?.onSectionBuilt?.(mainResource, currentHeadingLine);
            }
            currentHeading = null;
            currentProperties = {};
        };
        // ownerType for the observer: the type the current section's body
        // properties belong to, or undefined where the section names none.
        const currentOwnerType = () => {
            if (currentSection === 'node' || currentSection === 'sub_resource') {
                return currentHeading?.attributes.type;
            }
            return currentSection === 'resource' ? headerResourceType : undefined;
        };
        const storePending = () => {
            if (pendingMultiline && currentHeading) {
                // The one join per value: O(total length), not one per appended line.
                const value = pendingMultiline.lines.join('\n');
                const resolved = resolveDeprecatedProperty(currentOwnerType(), pendingMultiline.key, value);
                currentProperties[resolved.key] = resolved.value;
                observer?.onProperty?.({
                    section: currentSection,
                    ownerType: currentOwnerType(),
                    key: pendingMultiline.key,
                    value,
                    line: pendingMultiline.startLine,
                    isMultiline: pendingMultiline.lines.length > 1,
                    stored: resolved,
                });
            }
            pendingMultiline = null;
        };
        for (let i = 0; i < lines.length; i++) {
            // A `;` comment ends the line for Godot's reader wherever it sits, so it
            // is gone before the line is read as heading, property or continuation.
            const line = stripLineComment(lines[i], pendingMultiline?.scanState.inString ?? false);
            const lineNumber = i + 1;
            // Inside an open string, append raw lines, blank ones included. Only a real
            // section heading means the string never closed: salvage it and process the
            // heading. BBCode tags and bracketed content are string content, so
            // `isSectionHeading`, not `isHeading`, decides.
            if (pendingMultiline) {
                if (isSectionHeading(line)) {
                    storePending();
                }
                else {
                    // Scan only this chunk: a `\n` separator affects neither `inString` nor
                    // `depth`, so scanning `line` alone yields the state the joined string
                    // would.
                    pendingMultiline.lines.push(line);
                    pendingMultiline.scanState = scanValueChunk(line, pendingMultiline.scanState);
                    if (!isIncompleteState(pendingMultiline.scanState))
                        storePending();
                    continue;
                }
            }
            // A comment-only line is already whitespace: `stripLineComment` above is
            // the single place a `;` is understood.
            if (isEmpty(line)) {
                continue;
            }
            if (isHeading(line)) {
                finalizeSection();
                currentHeading = parseHeading(line);
                if (currentHeading) {
                    currentSection = this.identifySection(currentHeading);
                    currentHeadingLine = lineNumber;
                    if (currentHeading.type === 'gd_resource') {
                        headerResourceType = currentHeading.attributes.type;
                    }
                    const editablePath = currentHeading.type === 'editable' ? currentHeading.attributes.path : undefined;
                    if (editablePath !== undefined)
                        editableInstances.push(simplifiedNodePath(editablePath));
                    observer?.onSectionStart?.(currentHeading, currentSection, lineNumber);
                }
                else {
                    observer?.onError?.({
                        message: `Invalid heading format: "${line.trim()}"`,
                        line: lineNumber,
                        column: 1,
                        code: 'INVALID_HEADING_FORMAT',
                    });
                }
            }
            else {
                // A line that opens a bracket but never parses as a heading is malformed,
                // for example a missing ']'. The lenient path recovers below. Strict
                // consumers get the error.
                const malformedHeading = line.trim().startsWith('[');
                if (malformedHeading) {
                    observer?.onError?.({
                        message: `Invalid heading format: "${line.trim()}"`,
                        line: lineNumber,
                        column: 1,
                        code: 'INVALID_HEADING_FORMAT',
                    });
                }
                const property = parseProperty(line);
                if (!property) {
                    if (!malformedHeading) {
                        observer?.onError?.({
                            message: `Invalid property format: "${line.trim()}"`,
                            line: lineNumber,
                            column: 1,
                            code: 'INVALID_PROPERTY_FORMAT',
                        });
                    }
                    continue;
                }
                if (currentHeading) {
                    const scanState = scanValueChunk(property.value, INITIAL_SCAN_STATE);
                    if (isIncompleteState(scanState)) {
                        pendingMultiline = {
                            key: property.key,
                            lines: [property.value],
                            startLine: lineNumber,
                            scanState,
                        };
                    }
                    else {
                        // Stored under the name and literal the setter receives: a pre-4.0 alias like
                        // `frames` is `sprite_frames`, and `extents` is `size` doubled, so every
                        // reader sees one key and one value. The observer gets both as written,
                        // since a diagnostic names what is in the file, and the stored pair beside them.
                        const resolved = resolveDeprecatedProperty(currentOwnerType(), property.key, property.value);
                        currentProperties[resolved.key] = resolved.value;
                        observer?.onProperty?.({
                            section: currentSection,
                            ownerType: currentOwnerType(),
                            key: property.key,
                            value: property.value,
                            line: lineNumber,
                            isMultiline: false,
                            stored: resolved,
                        });
                    }
                }
            }
        }
        storePending(); // flush a string that ran to EOF unclosed
        finalizeSection();
        // Each lookup by id indexes a table once (`utils/keyedLookup.ts`), so a table never changes after the scan.
        Object.freeze(origins);
        Object.freeze(externalResources);
        Object.freeze(internalResources);
        const seating = seatScene(origins);
        logger.debug(`[Parser] Parsing complete: ${origins.length} nodes, ${externalResources.length} external resources, ${internalResources.length} internal resources`);
        return {
            scene: {
                nodes: seating.roots,
                externalResources,
                internalResources,
                refusesToInstantiate: refusesToInstantiate(origins),
                ...(headerResourceType !== undefined ? { resourceType: headerResourceType } : {}),
                ...(editableInstances.length > 0 ? { editableInstances } : {}),
                ...(mainResource ? { mainResource } : {}),
            },
            origins,
            seating,
        };
    }
    identifySection(heading) {
        if (!heading)
            return 'none';
        if (heading.type === 'node')
            return 'node';
        if (heading.type === 'ext_resource')
            return 'ext_resource';
        if (heading.type === 'sub_resource')
            return 'sub_resource';
        if (heading.type === 'resource')
            return 'resource';
        return 'none';
    }
}
//# sourceMappingURL=TscnParserCore.js.map